import { forwardRefPolymorphic, PolymorphicProps } from "@datamole/wds-std";
import clsx from "clsx";
import { type ButtonHTMLAttributes, type ElementType, type ForwardedRef } from "react";

import styles from "./button.module.css";
import { ButtonContext } from "./context";
import type { Size, Variant } from "./types";
import { DEFAULT_ELEMENT_TYPE, DEFAULT_SIZE, DEFAULT_VARIANT } from "./utils";

type Props<TAs extends ElementType = typeof DEFAULT_ELEMENT_TYPE> = {
    as?: TAs;
    size?: Size;
    variant?: Variant;
} & PolymorphicProps<TAs, typeof DEFAULT_ELEMENT_TYPE>;

/**
 * Buttons are used to trigger actions.
 */
const Button = <TAs extends ElementType>(props: Props<TAs>, ref: ForwardedRef<unknown>) => {
    const { as, children, className, size = DEFAULT_SIZE, variant = DEFAULT_VARIANT, ...otherProps } = props;

    const Component = as ?? DEFAULT_ELEMENT_TYPE;
    const isButtonElement = !as || as === DEFAULT_ELEMENT_TYPE;

    const defaultButtonProps = {
        ...((isButtonElement && { type: "button" }) as ButtonHTMLAttributes<HTMLButtonElement>),
    };

    return (
        <ButtonContext.Provider
            value={{
                size,
                variant,
            }}
        >
            <Component
                ref={ref}
                className={clsx(styles.button, className)}
                data-variant={variant}
                data-size={size}
                {...defaultButtonProps}
                {...otherProps}
            >
                {children}
            </Component>
        </ButtonContext.Provider>
    );
};

Button.displayName = "Button";

const ButtonWithPolymorphicRef = forwardRefPolymorphic(Button);

export { ButtonWithPolymorphicRef as Button };
export type { Props as ButtonProps };
